import { useInvestorDropdownSearch } from "@/application/queries";
import type { InvestorType, InvestorWithInvestmentSummary } from "@/domain/investor";
import { useOrganizationStore } from "@/stores/useOrganizationStore";
import { useCallback, useMemo, useState } from "react";

export interface InvestorDropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
  type?: "individual" | "company";
}

/** Minimal shape needed to preselect an investor in the dropdown. */
interface InvestorDropdownPreselection {
  uuid: string;
  display_name?: string;
  type: InvestorType;
}

interface UseInvestorDropdownOptions {
  enabled?: boolean;
  pageSize?: number;
  preSelectedItem?: InvestorDropdownPreselection | null;
}

interface UseInvestorDropdownReturn {
  options: InvestorDropdownOption[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  searchQuery: string;
  handleSearch: (query: string) => void;
  handleLoadMore: () => void;
  getInvestorByUuid: (uuid: string) => InvestorWithInvestmentSummary | undefined;
  reset: () => void;
}

export const useInvestorDropdown = (
  options: UseInvestorDropdownOptions = {},
): UseInvestorDropdownReturn => {
  const { enabled = true, pageSize = 20, preSelectedItem } = options;
  const orgId = useOrganizationStore((state) => state.selectedOrganizationUuid ?? "");
  const [searchQuery, setSearchQuery] = useState("");

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error: queryError,
  } = useInvestorDropdownSearch(orgId, searchQuery, { enabled, pageSize });

  const investors = useMemo(() => data?.pages.flatMap((p) => p.investors) ?? [], [data]);

  const dropdownOptions: InvestorDropdownOption[] = useMemo(() => {
    const opts: InvestorDropdownOption[] = investors.map((investor) => ({
      value: investor.uuid,
      label: investor.display_name,
      type: investor.type,
    }));
    if (preSelectedItem && !investors.some((i) => i.uuid === preSelectedItem.uuid)) {
      opts.unshift({
        value: preSelectedItem.uuid,
        label: preSelectedItem.display_name || "Sin nombre",
        type: preSelectedItem.type,
      });
    }
    return opts;
  }, [investors, preSelectedItem]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  const getInvestorByUuid = useCallback(
    (uuid: string): InvestorWithInvestmentSummary | undefined =>
      investors.find((i) => i.uuid === uuid),
    [investors],
  );

  const reset = useCallback(() => {
    setSearchQuery("");
  }, []);

  return {
    options: dropdownOptions,
    loading: isLoading,
    loadingMore: isFetchingNextPage,
    error: queryError ? "error.loading_investors" : null,
    hasMore: !!hasNextPage,
    searchQuery,
    handleSearch,
    handleLoadMore,
    getInvestorByUuid,
    reset,
  };
};
